import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { createClient } from '@supabase/supabase-js'
import { GlobalCookieConsent } from '../components/GlobalCookieConsent'

// Initialize Supabase with environment variables
// ✅ Using VITE_* variables (these are safe for frontend)
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseKey) {
  console.error('❌ Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY in environment variables')
}

const supabase = createClient(supabaseUrl || '', supabaseKey || '')

export default function AuthCallback() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Attendre que Supabase traite le callback (la session est créée automatiquement)
        await new Promise(resolve => setTimeout(resolve, 500))

        const { data, error: sessionError } = await supabase.auth.getSession()
        
        if (sessionError) {
          throw new Error(sessionError.message || 'Erreur de session')
        }

        const session = data?.session
        const user = session?.user

        if (!user || !session) {
          console.log('Session data:', { session, user })
          throw new Error('Pas de session utilisateur trouvée après OAuth')
        }
          
        // Appeler l'API backend pour créer/mettre à jour l'utilisateur
        try {
          const response = await fetch('https://lrv7zfe6dc7hrpucp3qulxlzaq0fszlw.lambda-url.eu-west-3.on.aws/auth/oauth-callback', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: user.email,
              provider: user.user_metadata?.provider || 'google',
              supabase_user_id: user.id,
              name: user.user_metadata?.full_name || user.email
            })
          })

          const backendData = await response.json()

          if (response.ok) {
            // Stocker les tokens Supabase
            localStorage.setItem('supabase_access_token', session.access_token)
            if (session.refresh_token) {
              localStorage.setItem('supabase_refresh_token', session.refresh_token)
            }
            
            // Stocker l'ID utilisateur et email
            localStorage.setItem('user_id', backendData.user_id || user.id)
            localStorage.setItem('email', user.email)
            
            // Stocker le JWT du backend si fourni
            if (backendData.access_token) {
              localStorage.setItem('access_token', backendData.access_token)
              localStorage.setItem('token_type', 'Bearer')
            }

            // Rediriger vers le dashboard
            navigate('/dashboard')
          } else {
            throw new Error(backendData.detail || 'Erreur du serveur')
          }
        } catch (backendError) {
          console.error('Backend OAuth sync error:', backendError)
          // Continuer quand même avec Supabase session
          localStorage.setItem('supabase_access_token', session.access_token)
          if (session.refresh_token) {
            localStorage.setItem('supabase_refresh_token', session.refresh_token)
          }
          localStorage.setItem('user_id', user.id)
          localStorage.setItem('email', user.email)
          
          // Rediriger quand même au dashboard
          navigate('/dashboard')
        }
      } catch (err) {
        console.error('OAuth callback error:', err)
        setError(err.message || 'Erreur lors de la connexion. Veuillez réessayer.')
        
        // Rediriger vers la page de connexion après 3 secondes
        setTimeout(() => {
          navigate('/login')
        }, 3000)
      } finally {
        setLoading(false)
      }
    }

    handleCallback()
  }, [navigate])

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
      <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-500/25 blur-3xl hidden md:block" />
      <div className="pointer-events-none absolute bottom-[-90px] right-[-90px] h-96 w-96 rounded-full bg-sky-500/20 blur-3xl hidden md:block" />

      <div className="relative z-10 flex min-h-screen items-center justify-center p-4">
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/5 p-8 shadow-[0_0_1px_rgba(255,255,255,0.25),0_25px_80px_-20px_rgba(16,185,129,0.35)] backdrop-blur-2xl text-center">
          {loading ? (
            <>
              <div className="mb-4 flex justify-center">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-white/20 border-t-emerald-500"></div>
              </div>
              <h2 className="text-lg font-semibold">Connexion en cours...</h2>
              <p className="mt-2 text-sm text-slate-400">Veuillez patienter pendant que nous finalizons votre authentification.</p>
            </>
          ) : error ? (
            <>
              <div className="mb-4 text-center">
                <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-500/20 flex items-center justify-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>
              <h2 className="text-lg font-semibold text-red-400">Erreur de connexion</h2>
              <p className="mt-2 text-sm text-slate-400">{error}</p>
              <p className="mt-4 text-xs text-slate-500">Redirection vers la connexion dans quelques secondes...</p>
            </>
          ) : null}
        </div>
      </div>

      {/* Cookie Consent */}
      <GlobalCookieConsent />
    </div>
  )
}
